import type { WithdrawalAdminDto, WithdrawalUserDto, WithdrawalWalletDto } from '@shared/api/contracts';
import { mapWithdrawalPolicyFields } from '@shared/api/lib/map-withdrawal-policy';
import type { Withdrawal, WithdrawalWallet } from '../model/types';

const normalizeCreatedAt = (dto: WithdrawalUserDto): string =>
  dto.createdAt ?? dto.created_at ?? new Date().toISOString();

const mapWalletDto = (wallet?: WithdrawalWalletDto): WithdrawalWallet | undefined => {
  if (!wallet) return undefined;

  return {
    id: String(wallet.id),
    name: wallet.name,
    amount: wallet.amount != null ? Number(wallet.amount) : undefined,
    groupName: wallet.groupName,
  };
};

const PARTNER_WALLET_GROUP = 'Партнерка';

/** Admin list has wallet.name `partner-{userId}` and group «Партнерка». User list and card view do not. */
const walletKindFromAdmin = (dto: WithdrawalAdminDto): 'merchant' | 'partner' | null => {
  if (dto.walletKind === 'merchant' || dto.walletKind === 'partner') return dto.walletKind;
  if (!dto.wallet) return null;
  const userId = dto.user?.id;
  if (userId != null && dto.wallet.name === `partner-${userId}`) return 'partner';
  if (dto.wallet.groupName === PARTNER_WALLET_GROUP) return 'partner';
  return 'merchant';
};

export const mapWithdrawalDtoToEntity = (dto: WithdrawalUserDto): Withdrawal => ({
  id: String(dto.id),
  network: dto.network,
  address: dto.address,
  amount: String(dto.amount),
  assetCode: dto.assetCode ?? 'USDT',
  status: dto.status,
  state: dto.state ?? null,
  stateVersion: dto.stateVersion ?? null,
  statusChangedAt: dto.statusChangedAt ?? null,
  completedAt: dto.completedAt ?? null,
  reasonCode: dto.reasonCode ?? null,
  fundsReservationState: dto.fundsReservationState ?? null,
  confirmations: dto.confirmations ?? null,
  walletKind: dto.walletKind ?? null,
  txid: dto.txid,
  netPayoutAmount: dto.netPayoutAmount ?? null,
  ledgerOperationId: dto.ledgerOperationId != null ? String(dto.ledgerOperationId) : null,
  networkCost: dto.networkCost ?? null,
  comment: dto.comment,
  createdAt: normalizeCreatedAt(dto),
  updatedAt: dto.updatedAt,
  wallet: mapWalletDto(dto.wallet),
  ...mapWithdrawalPolicyFields(dto),
});

export const mapAdminWithdrawalDtoToEntity = (dto: WithdrawalAdminDto): Withdrawal => ({
  ...mapWithdrawalDtoToEntity(dto),
  walletKind: walletKindFromAdmin(dto),
  userEmail: dto.user?.email,
  userId: dto.user?.id != null ? String(dto.user.id) : undefined,
  adminEmail: dto.admin?.email,
  adminId: dto.admin?.id != null ? String(dto.admin.id) : undefined,
  makerKind: dto.makerKind,
  makerUserId: dto.makerUserId != null ? String(dto.makerUserId) : undefined,
});
